import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import api from '@/lib/api';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import {
  PROHIBITED_LAYER_OPTIONS,
  SFX_INTENSITY_VALUES,
  capReachedMessage,
  formatSoundMs,
  precisionDisplay,
  sceneIdsFromEvents,
  shouldShowSoundPanel,
} from '@/lib/storySoundPanel';

function apiError(err, fallback) {
  return capReachedMessage(err?.response?.data)
    || err?.response?.data?.detail
    || err?.message
    || fallback;
}

export function useStorySoundGate(storyId, client = api) {
  const [meta, setMeta] = useState({ engineEnabled: false, hasPlan: false, payload: null });
  const refresh = useCallback(async () => {
    if (!storyId) return;
    try {
      const { data } = await client.get(`/stories/${storyId}/sound-events`);
      setMeta({
        engineEnabled: Boolean(data?.engine_enabled),
        hasPlan: Boolean(data?.has_plan),
        payload: data,
      });
    } catch {
      setMeta({ engineEnabled: false, hasPlan: false, payload: null });
    }
  }, [client, storyId]);
  useEffect(() => { refresh(); }, [refresh]);
  return {
    ...meta,
    visible: shouldShowSoundPanel(meta),
    refresh,
  };
}

export function StorySoundToggle({ visible, open, onOpenChange }) {
  if (!visible) return null;
  return (
    <div
      className="flex items-center gap-2 rounded-lg border px-3 py-1.5"
      style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}
    >
      <Switch
        id="story-sound-panel-toggle"
        checked={Boolean(open)}
        onCheckedChange={onOpenChange}
        data-testid="story-sound-panel-toggle"
      />
      <Label
        htmlFor="story-sound-panel-toggle"
        className="cursor-pointer text-sm font-medium whitespace-nowrap"
        style={{ color: 'var(--text-primary)' }}
      >
        Sound
      </Label>
    </div>
  );
}

export function StorySoundPanel({
  storyId,
  open = true,
  client = api,
  payload: payloadProp,
}) {
  const [payload, setPayload] = useState(payloadProp || null);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState('');
  const [eventBusy, setEventBusy] = useState({});
  const [levels, setLevels] = useState({});
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!storyId) return;
    try {
      const { data } = await client.get(`/stories/${storyId}/sound-events`);
      setPayload(data);
      setLoadError('');
      const nextLevels = {};
      for (const event of data.events || []) {
        nextLevels[event.event_id] = event?.mix?.target_level_db ?? '';
      }
      setLevels(nextLevels);
    } catch (err) {
      setLoadError(apiError(err, 'Could not load sound events'));
    }
  }, [client, storyId]);

  useEffect(() => {
    if (payloadProp) {
      setPayload(payloadProp);
      return;
    }
    load();
  }, [load, payloadProp]);

  const visible = shouldShowSoundPanel({
    engineEnabled: payload?.engine_enabled,
    hasPlan: payload?.has_plan,
  });
  const events = payload?.events || [];
  const scenes = useMemo(() => sceneIdsFromEvents(events), [events]);
  const direction = payload?.audio_direction || { include_sfx: true, sfx_intensity: 'moderate', prohibited_layers: [] };
  const qa = payload?.qa_summary || {};

  const run = async (key, fn) => {
    setBusy(key);
    setError('');
    setNotice('');
    try {
      await fn();
      setNotice('success');
      await load();
    } catch (err) {
      const cap = capReachedMessage(err?.response?.data) || capReachedMessage(err?.response?.data?.detail);
      setError(cap || apiError(err, 'Request failed'));
      setNotice('failure');
    } finally {
      setBusy('');
    }
  };

  const runEvent = async (eventId, key, fn) => {
    setEventBusy((prev) => ({ ...prev, [eventId]: key }));
    setError('');
    setNotice('');
    try {
      await fn();
      setNotice('success');
      await load();
    } catch (err) {
      const cap = capReachedMessage(err?.response?.data) || capReachedMessage(String(err?.response?.data?.detail || ''));
      setError(cap || apiError(err, 'Request failed'));
      setNotice('failure');
    } finally {
      setEventBusy((prev) => {
        const next = { ...prev };
        delete next[eventId];
        return next;
      });
    }
  };

  const saveDirection = (patch) => run('direction', () => client.patch(`/stories/${storyId}/audio-direction`, patch));

  if (!open || !visible) return null;

  return (
    <section
      className="border-b px-4 sm:px-6 py-4"
      style={{ borderColor: 'var(--glass-border)', background: 'rgba(13, 16, 22, 0.88)' }}
      data-testid="story-sound-panel"
    >
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#9CA3AF]">Sound</p>
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-[#62FAE3]" />
              Event review
              <span
                className="text-[11px] font-mono uppercase tracking-wide rounded-md border px-2 py-0.5 text-[#E5E7EB]"
                style={{ borderColor: 'var(--glass-border)' }}
                data-testid="sound-plan-status"
              >
                {payload?.status || 'pending'}
              </span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {scenes.map((sceneId) => (
              <Button
                key={sceneId}
                type="button"
                size="sm"
                variant="outline"
                className="h-8 text-xs rounded-lg"
                disabled={Boolean(busy)}
                onClick={() => run(`scene-${sceneId}`, () => client.post(`/stories/${storyId}/scenes/${sceneId}/soundtrack/regenerate`, { mode: 'both' }))}
                data-testid={`sound-scene-regenerate-${sceneId}`}
              >
                {busy === `scene-${sceneId}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Regenerate scene {sceneId}
              </Button>
            ))}
            <Button
              type="button"
              size="sm"
              className="h-8 text-xs rounded-lg"
              disabled={Boolean(busy)}
              onClick={() => run('soundtrack', () => client.post(`/stories/${storyId}/soundtrack/regenerate`, { mode: 'both' }))}
              data-testid="sound-soundtrack-regenerate"
            >
              {busy === 'soundtrack' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Regenerate soundtrack
            </Button>
          </div>
        </div>

        {loadError ? <p className="text-xs text-red-300" data-testid="sound-load-error">{loadError}</p> : null}
        {error ? <p className="text-xs text-red-300" data-testid="sound-action-error">{error}</p> : null}
        {notice ? (
          <p className="text-[11px] text-[#9CA3AF]" data-testid="sound-action-status">{busy ? 'pending' : notice}</p>
        ) : null}

        <div
          className="rounded-xl border p-3 text-xs text-[#E5E7EB] space-y-1"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}
          data-testid="sound-qa-summary"
        >
          <p>Warnings: {(qa.warnings || []).length}</p>
          <p>Approximate events: {(qa.approximate || []).length} (unconfirmed timing)</p>
          {(qa.category_mismatches || []).length ? (
            <p data-testid="sound-qa-category-mismatch">Category mismatches: {qa.category_mismatches.length}</p>
          ) : null}
        </div>

        <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: 'var(--glass-border)' }} data-testid="sound-audio-direction">
          <p className="text-[10px] uppercase tracking-widest text-[#9CA3AF]">Audio Direction</p>
          <label className="flex items-center gap-2 text-sm text-white">
            <input
              type="checkbox"
              checked={direction.include_sfx !== false}
              onChange={(e) => saveDirection({ include_sfx: e.target.checked })}
              data-testid="sound-include-sfx"
            />
            Include SFX
          </label>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-[#9CA3AF]">SFX intensity</span>
            {SFX_INTENSITY_VALUES.map((value) => (
              <Button
                key={value}
                type="button"
                size="sm"
                variant={direction.sfx_intensity === value ? 'default' : 'outline'}
                className="h-7 text-xs rounded-lg"
                onClick={() => saveDirection({ sfx_intensity: value })}
                data-testid={`sound-intensity-${value}`}
              >
                {value}
              </Button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-1.5" data-testid="sound-prohibited-layers">
            {PROHIBITED_LAYER_OPTIONS.map((layer) => {
              const checked = (direction.prohibited_layers || []).includes(layer.id);
              return (
                <label key={layer.id} className="flex items-center gap-2 text-xs text-[#E5E7EB]">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const next = new Set(direction.prohibited_layers || []);
                      if (e.target.checked) next.add(layer.id);
                      else next.delete(layer.id);
                      saveDirection({ prohibited_layers: Array.from(next) });
                    }}
                    data-testid={`sound-prohibit-${layer.id}`}
                  />
                  Prohibit {layer.label}
                </label>
              );
            })}
          </div>
        </div>

        <div className="space-y-2" data-testid="sound-event-list">
          {events.map((event) => {
            const precision = precisionDisplay(event.precision);
            const previewUrl = mediaUrlForPlayback(event?.asset?.url);
            const rowBusy = eventBusy[event.event_id];
            return (
              <article
                key={event.event_id}
                className="rounded-xl border p-3 space-y-2"
                style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}
                data-testid={`sound-event-row-${event.event_id}`}
              >
                <div className="flex flex-wrap justify-between gap-2">
                  <div>
                    <p className="text-sm text-white font-medium">{event.event_type} · {event.category}</p>
                    <p className="text-xs text-[#9CA3AF]">{event.description}</p>
                  </div>
                  <span className="text-[11px] font-mono text-[#E5E7EB]">{event.status}</span>
                </div>
                <dl className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-[#9CA3AF]">
                  <div>Scene <span className="text-[#E5E7EB]">{event.scene_id}</span></div>
                  <div>Anchor <span className="text-[#E5E7EB]">{event.anchor_source || '—'}</span></div>
                  <div>
                    Precision{' '}
                    <span
                      className="text-[#E5E7EB]"
                      data-testid={`sound-event-precision-${event.event_id}`}
                      data-confirmed={precision.confirmed ? 'yes' : 'no'}
                    >
                      {precision.label}
                    </span>
                  </div>
                  <div>Start/end <span className="text-[#E5E7EB]">{formatSoundMs(event.start_ms)} – {formatSoundMs(event.end_ms)}</span></div>
                  <div>Provider <span className="text-[#E5E7EB]">{event?.asset?.provider || event?.asset?.source || '—'}</span></div>
                </dl>
                {(event.qa_warnings || []).length ? (
                  <ul className="text-[11px] text-amber-200 list-disc pl-4" data-testid={`sound-event-warnings-${event.event_id}`}>
                    {event.qa_warnings.map((row, index) => (
                      <li key={index}>{row.reason || JSON.stringify(row)}</li>
                    ))}
                  </ul>
                ) : null}
                {previewUrl ? (
                  <audio controls className="w-full h-8" src={previewUrl} data-testid={`sound-event-preview-${event.event_id}`} />
                ) : (
                  <p className="text-[11px] text-[#9CA3AF]">No generated clip to preview</p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs rounded-lg"
                    onClick={() => runEvent(event.event_id, 'enable', () => client.patch(
                      `/stories/${storyId}/sound-events/${event.event_id}`,
                      { enabled: !event.enabled },
                    ))}
                    data-testid={`sound-event-toggle-${event.event_id}`}
                  >
                    {event.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <label className="flex items-center gap-1 text-[11px] text-[#9CA3AF]">
                    Level
                    <input
                      type="number"
                      className="h-7 w-16 rounded-md border bg-transparent px-1 text-[#E5E7EB]"
                      style={{ borderColor: 'var(--glass-border)' }}
                      value={levels[event.event_id] ?? ''}
                      onChange={(e) => setLevels((prev) => ({ ...prev, [event.event_id]: e.target.value }))}
                      data-testid={`sound-event-level-${event.event_id}`}
                    />
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs rounded-lg"
                    onClick={() => runEvent(event.event_id, 'level', () => client.patch(
                      `/stories/${storyId}/sound-events/${event.event_id}`,
                      { target_level_db: Number(levels[event.event_id]) },
                    ))}
                    data-testid={`sound-event-level-save-${event.event_id}`}
                  >
                    Save level
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs rounded-lg"
                    disabled={Boolean(rowBusy)}
                    onClick={() => runEvent(event.event_id, 'regen', () => client.post(
                      `/stories/${storyId}/sound-events/${event.event_id}/regenerate`,
                      { mode: 'asset' },
                    ))}
                    data-testid={`sound-event-regenerate-${event.event_id}`}
                  >
                    {rowBusy === 'regen' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Regenerate
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default StorySoundPanel;
